import React, { useEffect, useState } from "react";
import api from "../../services/api";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";

import "../../styles/floatingForm.css";

const CreateUpdateFaqs = () => {
  const navigate = useNavigate();

  // -----------------------------------
  // GET PAGE URL FROM ROUTE
  // -----------------------------------

  const { pageUrl } = useParams();

  const isEditMode = Boolean(pageUrl);

  const decodedPageUrl = isEditMode
    ? decodeURIComponent(pageUrl)
    : "";


  // -----------------------------------
  // FORM STATE
  // -----------------------------------

  const [form, setForm] = useState({
    page_url: "",
    faqs: "",
  });

  const [errors, setErrors] = useState({});

  const [loading, setLoading] = useState(false);


  // -----------------------------------
  // FETCH FAQ DETAILS
  // -----------------------------------

  const fetchDetails = async () => {
    try {
      const res = await api.post(
        "/api/Faqurl/getbyurl",
        {
          page_url: decodedPageUrl,
        }
      );

      if (res.data.status === "success") {

        const faqs = res.data.data
          .map(
            (item) =>
              `Q: ${item.question}\nA: ${item.answer}`
          )
          .join("\n\n");

        setForm({
          page_url: decodedPageUrl,
          faqs,
        });

      }

    } catch (error) {

      console.error(
        "Failed to load FAQs:",
        error
      );

      toast.error("Failed to load FAQs");

    }
  };


  // -----------------------------------
  // LOAD EDIT DATA
  // -----------------------------------

  useEffect(() => {

    if (isEditMode) {
      fetchDetails();
    }

  }, [pageUrl]);


  // -----------------------------------
  // VALIDATION
  // -----------------------------------

  const validate = () => {

    const e = {};

    if (!form.page_url.trim()) {
      e.page_url = "Page URL is required";
    }

    if (!form.faqs.trim()) {
      e.faqs = "FAQs are required";
    }

    setErrors(e);

    return Object.keys(e).length === 0;
  };


  // -----------------------------------
  // PARSE FAQ TEXT
  // -----------------------------------

  const parseFaqs = (text) => {

    const lines = text.split("\n");

    const faqs = [];

    let currentQuestion = "";
    let currentAnswer = "";
    let mode = null;


    lines.forEach((line) => {

      const trimmed = line.trim();

      if (!trimmed) return;


      // QUESTION

      if (/^Q\s*:/i.test(trimmed)) {

        // Save previous FAQ

        if (
          currentQuestion ||
          currentAnswer
        ) {

          faqs.push({
            question:
              currentQuestion.trim(),

            answer:
              currentAnswer.trim(),
          });

        }


        currentQuestion =
          trimmed.replace(
            /^Q\s*:\s*/i,
            ""
          );

        currentAnswer = "";

        mode = "question";

      }


      // ANSWER

      else if (/^A\s*:/i.test(trimmed)) {

        currentAnswer =
          trimmed.replace(
            /^A\s*:\s*/i,
            ""
          );

        mode = "answer";

      }


      // CONTINUATION

      else {

        if (mode === "question") {

          currentQuestion +=
            "\n" + trimmed;

        }

        else if (mode === "answer") {

          currentAnswer +=
            "\n" + trimmed;

        }

      }

    });


    // Save last FAQ

    if (
      currentQuestion ||
      currentAnswer
    ) {

      faqs.push({
        question:
          currentQuestion.trim(),

        answer:
          currentAnswer.trim(),
      });

    }


    return faqs;
  };


  // -----------------------------------
  // SUBMIT
  // -----------------------------------

  const submitData = async (e) => {

    e.preventDefault();


    if (!validate()) {
      return;
    }


    const faqs = parseFaqs(
      form.faqs
    );


    if (!faqs.length) {

      toast.error(
        "Please add at least one FAQ or check the format."
      );

      return;
    }


    const invalid = faqs.find(
      (item) =>
        !item.question ||
        !item.answer
    );


    if (invalid) {

      toast.error(
        "Every Q: must have an A:."
      );

      return;
    }


    setLoading(true);


    const formData = new FormData();

    formData.append(
      "page_url",
      form.page_url
    );


    faqs.forEach((faq) => {

      formData.append(
        "question[]",
        faq.question
      );

      formData.append(
        "answer[]",
        faq.answer
      );

    });


    try {

      let res;


      // -----------------------------------
      // UPDATE
      // -----------------------------------

      if (isEditMode) {

        res = await api.post(
          "/api/Faqurl/update",
          formData
        );

      }

      // -----------------------------------
      // CREATE
      // -----------------------------------

      else {

        res = await api.post(
          "/api/Faqurl/add",
          formData
        );

      }


      if (
        res.data.status ===
        "success"
      ) {

        toast.success(
          `FAQ ${
            isEditMode
              ? "updated"
              : "created"
          } successfully`
        );


        // GO BACK TO LIST

        navigate("/manage-faqs");

      }

      else {

        toast.error(
          "Something went wrong."
        );

      }

    } catch (err) {

      console.error(
        "Failed to save FAQ:",
        err
      );

      toast.error(
        "Failed to save FAQ."
      );

    } finally {

      setLoading(false);

    }

  };


  // -----------------------------------
  // CANCEL
  // -----------------------------------

  const handleCancel = () => {

    navigate("/manage-faqs");

  };

  return (
    <div className="floating-form-container"  >
      <div className="heading">
        <h2>{isEditMode ? "Update FAQ" : "Create FAQ"}</h2>
      </div>

      <form onSubmit={submitData}>
        <div className="form-container overflow-y-scroll">
            <div className="section-1">
                <div className="input-container-floating-form wid-100">
                    <label>Page URL *</label>

                    <input
                    type="text"
                    value={form.page_url}
                    onChange={(e) =>
                        setForm({
                        ...form,
                        page_url: e.target.value,
                        })
                    }
                    className={errors.page_url ? "is-invalid" : ""}
                    />

                    {errors.page_url && (
                    <span className="text-danger small">
                        {errors.page_url}
                    </span>
                    )}
                </div>

                <div className="input-container-floating-form wid-100">
                    <label>FAQs *</label>

                    <small
                    style={{
                        display: "block",
                        marginBottom: 10,
                        color: "#777",
                    }}
                    >
                    Format:
                    <br />
                    Q: Question
                    <br />
                    A: Answer
                    <br />
                    <br />
                    Q: Question
                    <br />
                    A: Answer
                    </small>

                    <textarea
                    rows={16}
                    value={form.faqs}
                    onChange={(e) =>
                        setForm({
                        ...form,
                        faqs: e.target.value,
                        })
                    }
                    className={errors.faqs ? "is-invalid" : ""}
                    placeholder=""
                    />

                    {errors.faqs && (
                    <span className="text-danger small">
                        {errors.faqs}
                    </span>
                    )}
                </div>

                </div>
        <div className="form-btn-container">
          <button type="submit">{isEditMode ? "Update" : "Create"}</button>
          <button type="reset" onClick={handleCancel}>Cancel</button>
        </div>
        </div>
      </form>
    </div>
  );
};

export default CreateUpdateFaqs;
